<template>
  <div class="form-item-container">
    <div class="questions-container">
      <div class="question-primary-language">
        {{ question_lang1 }}
      </div>
      <div class="question-secondary-language" v-if="bilingue">
        {{ question_lang2 }}
      </div>
    </div>
    <div class="response-container-two-options" v-if="options.length === 2">
      <div class="option-container" v-for="(option, index) in options" :key="index"
        @click="handleOptionClick(index)" :class="{ 'clicked': clickedOption === index }">
        <span class="option-primary-language">{{ option.text_lang1 }}</span>
        <span class="option-secondary-language" v-if="bilingue">{{ option.text_lang2 }}</span>
      </div>
    </div>
    <div class="response-container-multi-options" v-if="options.length > 2">
      <div v-for="(option, index) in options" :key="index" @click="handleOptionClick(index)"
        class="option-container">
        <div class="option-id" :class="{ 'clicked': clickedOption === index }">
          <span>{{ optionsLetter[index] }}</span>
        </div>
        <div class="option-text">
          <span class="option-primary-language">{{ option.text_lang1 }}</span>
          <span class="option-secondary-language" v-if="bilingue">{{ option.text_lang2 }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { FormItemOption } from '@/utils/DataTypes'
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'GridItem',
  props: {
    question_lang1: {
      type: String,
      default: ''
    },
    question_lang2: {
      type: String,
      default: ''
    },
    bilingue: {
      type: Boolean,
      default: false
    },
    options: {
      type: Array as () => FormItemOption[],
      default: () => []
    },
    form_item_id: {
      type: Number,
      default: 0
    },
    form_item_order: {
      type: Number,
      default: -1
    }
  },
  data () {
    return {
      clickedOption: null as number | null,
      optionsLetter: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O']
    }
  },
  methods: {
    handleOptionClick (index: number) {
      this.clickedOption = index
    }
  }
})
</script>

<style lang="scss" scoped>
.form-item-container {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr;

  width: 100%;

  padding: 2em 5% 2em 5%;

  color: white;

  .questions-container {
    grid-row: 1;

    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr auto;

    align-items: center;
    justify-items: center;

    padding-bottom: 2em;

    .question-primary-language {
      grid-row: 1;
      font-size: clamp(1.5em, 1vw + 1rem, 2em);
      font-weight: bold;
      padding-bottom: 0.2em;
      text-align: center;
    }

    .question-secondary-language {
      grid-row: 2;
      font-size: clamp(1em, 0.5vw + 1rem, 1.5em);
      text-align: center;
    }
  }

  .response-container-two-options {
    grid-row: 2;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1em;

    padding: 1em;

    .option-container {
      display: flex;
      flex-wrap: wrap;
      flex-direction: row;

      justify-content: center;

      padding: 1em 2em;

      border: 1px solid white;
      border-radius: 10px;
      transition: background-color 0.3s ease;
      transition: color 0.3s ease;

      cursor: pointer;

      color: #fff;

      .option-primary-language {
        width: 100%;
        font-size: clamp(1.2em, 1vw + 1rem, 1.5em);
        font-weight: bold;
        text-align: center;
      }

      .option-secondary-language {
        width: 100%;
        font-size: clamp(1.2em, 0.5vw + 1rem, 1.5em);
        text-align: center;

      }

      &.clicked {
        color: black !important;
        background-color: #fff;
      }
    }
  }

  .response-container-multi-options {
    grid-row: 2;

    display: flex;
    flex-direction: column;

    width: 100%;
    padding: 1em;

    gap: 20px;
    line-height: 40px;
    font-size: 20px;
    border-radius: 40px;
    justify-self: center;

    .option-container {
      display: grid;
      grid-template-columns: 15% 1fr;

      .option-id {
        grid-column: 1;
        width: 100%;
        background-color: #979797;
        /* Default background color */
        transition: background-color 0.3s ease;
        transition: color 0.3s ease;
        display: flex;
        align-items: center;
        border-bottom-left-radius: 10px;
        border-top-left-radius: 10px;
        justify-content: center;
        font-size: 2em;
        font-weight: bold;

        padding: 1rem;
      }

      .option-text {
        grid-column: 2;
        cursor: pointer;

        display: flex;
        flex-wrap: wrap;
        flex-direction: row;
        width: 100%;
        border-radius: 0px 10px 10px 0px;

        background-color: rgba(255, 255, 255, 0.07);
        transition: background-color 0.3s ease;
        transition: color 0.3s ease;

        padding: 1rem;
        align-items: center;

        .option-primary-language {
          width: 100%;
          color: #fff;
          font-size: clamp(1.2em, 1vw + 1rem, 1.5em);
          text-align: center;
        }

        .option-secondary-language {
          width: 100%;
          color: #fff;
          font-size: clamp(1em, 0.5vw + 1rem, 1.5em);
          text-align: center;

        }
      }
    }

    .option-container:has(.option-id.clicked) {
      border-radius: 10px;
      border: 1px solid #fff;
    }

    .options div.clicked,
    .option-container .option-id.clicked {
      border: solid 1px #979797;
      background-color: #fff;
      color: black;
    }
  }
}

@media (max-width: 1024px) {
  .form-item-container {

    .response-container-two-options {
      grid-row: 2;
      display: flex;
      flex-direction: column;
      gap: 1em;

      justify-content: center;

      .option-container {
        display: flex;
        flex-wrap: wrap;
        flex-direction: row;
        align-self: center;

        padding: 1em 0em;
        margin: 0em 1em;
        min-width: 80%;
        max-width: 90%;
      }
    }

    .response-container-multi-options {
      grid-row: 2;

      display: flex;
      flex-direction: column;

      width: 100%;
      justify-self: center;

      .option-container {
        display: grid;
        grid-template-columns: 15% 1fr;

        .option-id {
          grid-column: 1;
          width: 100%;
          background-color: #979797;
          transition: background-color 0.3s ease;
          transition: color 0.3s ease;

          display: flex;
          align-items: center;

          border-bottom-left-radius: 10px;
          border-top-left-radius: 10px;
          justify-content: center;
          font-size: clamp(2em, 1vw + 1rem, 5em);
          font-weight: bold;
        }

        .option-text {
          grid-column: 2;
          cursor: pointer;

          display: flex;
          flex-wrap: wrap;
          flex-direction: row;
          width: 100%;
          border-radius: 0px 10px 10px 0px;

          background-color: rgba(255, 255, 255, 0.07);
          transition: background-color 0.3s ease;
          transition: color 0.3s ease;

          .option-primary-language {
            width: 100%;
            color: #fff;
            text-align: center;
          }

          .option-secondary-language {
            width: 100%;
            color: #fff;
            text-align: center;

          }
        }
      }

      .option-container:has(.option-id.clicked) {
        border-radius: 10px;
        border: 1px solid #fff;
      }

      .options div.clicked,
      .option-container .option-id.clicked {
        border: solid 1px #979797;
        background-color: #fff;
        color: black;
      }
    }
  }
}
</style>
